import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import MiniGraph from '../lineGraph/mini';

@observer
class RaceWeatherSummary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            weatherSummary: null,
            loading: true,
            error: null,
            containerWidth: 350, // Default fallback
        };
        this.containerRef = null;
    }

    async loadWeatherSummary() {
        const { raceId, stores } = this.props;
        const { gpsPointStore } = stores;

        try {
            this.setState({ loading: true, error: null });
            const result = await gpsPointStore.getRaceWeatherSummary({ raceId });

            if (result && result.status === 200 && result.data) {
                this.setState({ weatherSummary: result.data, loading: false }, () => {
                    // Re-measure after data loads since container may now be rendered
                    setTimeout(() => this.measureContainer(), 0);
                });
            } else {
                this.setState({ weatherSummary: null, loading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, loading: false });
        }
    }

    componentDidMount() {
        this.loadWeatherSummary();
        this.measureContainer();
        window.addEventListener('resize', this.measureContainer);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.measureContainer);
    }

    measureContainer = () => {
        if (this.containerRef) {
            const width = this.containerRef.offsetWidth - 20; // Subtract padding
            this.setState({ containerWidth: Math.max(200, width) });
        }
    }

    // Calculate icon X position to match SVG data point positions
    // The SVG data spans from x=0 to x=width in SVG coordinates
    // With CSS padding-left on the SVG, the visual positions are from paddingLeft to paddingLeft+width
    // But we also need to account for the x-axis tick area which is narrower
    getIconXPosition(index, totalPoints, graphWidth, paddingLeft, paddingRight) {
        const minX = 0;
        const maxX = totalPoints - 1;
        if (maxX === minX) return paddingLeft;
        // Match the x-axis tick positioning: starts at paddingLeft, spans (graphWidth - paddingLeft - paddingRight)
        const effectiveWidth = graphWidth - paddingLeft - paddingRight;
        const x = paddingLeft + (index - minX) * effectiveWidth / (maxX - minX);
        return x;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.raceId !== this.props.raceId) {
            this.loadWeatherSummary();
        }
    }

    // Downsample data by averaging points in groups
    downsampleData(records, targetPoints = 30) {
        if (records.length <= targetPoints) {
            return records;
        }

        const groupSize = Math.ceil(records.length / targetPoints);
        const downsampled = [];

        for (let i = 0; i < records.length; i += groupSize) {
            const group = records.slice(i, Math.min(i + groupSize, records.length));
            const avgTemp = group.reduce((sum, r) => sum + (r.temperature || 0), 0) / group.length;
            const avgWind = group.reduce((sum, r) => sum + (r.windSpeed || 0), 0) / group.length;
            // Average precipitation for the group (represents typical precipitation for this time window)
            const avgPrecip = group.reduce((sum, r) => sum + (r.precipitation || 0), 0) / group.length;
            // Use the middle record for time and symbol
            const middleRecord = group[Math.floor(group.length / 2)];
            downsampled.push({
                timeWindow: middleRecord.timeWindow,
                temperature: avgTemp,
                windSpeed: avgWind,
                precipitation: avgPrecip,
                symbolCode: middleRecord.symbolCode,
            });
        }

        return downsampled;
    }

    prepareGraphData() {
        const { weatherSummary } = this.state;
        if (!weatherSummary?.records || weatherSummary.records.length === 0) {
            return { tempData: [], windData: [], precipData: [], symbolMarkers: [] };
        }

        // Downsample to ~30 points for smoother graph
        const records = this.downsampleData(weatherSummary.records, 30);

        const tempData = records.map((r, i) => ({
            epoch: new Date(r.timeWindow).getTime(),
            x: i,
            y: r.temperature,
        }));

        const windData = records.map((r, i) => ({
            epoch: new Date(r.timeWindow).getTime(),
            x: i,
            y: r.windSpeed,
        }));

        const precipData = records.map((r, i) => ({
            epoch: new Date(r.timeWindow).getTime(),
            x: i,
            y: r.precipitation || 0,
        }));

        // Select weather symbols at intervals (show ~6-8 icons)
        const symbolInterval = Math.max(1, Math.floor(records.length / 6));
        const symbolMarkers = [];
        for (let i = 0; i < records.length; i += symbolInterval) {
            const record = records[i];
            symbolMarkers.push({
                index: i,
                symbolCode: record.symbolCode,
                time: record.timeWindow,
                temp: record.temperature,
            });
        }
        // Ensure we include the last record if not already included
        const lastIdx = records.length - 1;
        if (symbolMarkers.length === 0 || symbolMarkers[symbolMarkers.length - 1].index !== lastIdx) {
            const lastRecord = records[lastIdx];
            symbolMarkers.push({
                index: lastIdx,
                symbolCode: lastRecord.symbolCode,
                time: lastRecord.timeWindow,
                temp: lastRecord.temperature,
            });
        }

        return { tempData, windData, precipData, symbolMarkers };
    }

    getWeatherIcon(symbolCode) {
        if (!symbolCode) return 'fa-solid fa-cloud';
        // Map Yr symbol codes to Font Awesome icons
        const iconMap = {
            clearsky: 'fa-solid fa-sun',
            fair: 'fa-solid fa-cloud-sun',
            partlycloudy: 'fa-solid fa-cloud-sun',
            cloudy: 'fa-solid fa-cloud',
            rain: 'fa-solid fa-cloud-rain',
            lightrain: 'fa-solid fa-cloud-rain',
            heavyrain: 'fa-solid fa-cloud-showers-heavy',
            snow: 'fa-solid fa-snowflake',
            lightsnow: 'fa-solid fa-snowflake',
            heavysnow: 'fa-solid fa-snowflake',
            sleet: 'fa-solid fa-cloud-meatball',
            fog: 'fa-solid fa-smog',
            thunder: 'fa-solid fa-bolt',
        };

        // Extract base symbol (remove _day/_night suffix)
        const baseSymbol = symbolCode.replace(/_day|_night/g, '');
        return iconMap[baseSymbol] || 'fa-solid fa-cloud';
    }

    getWeatherIconColor(symbolCode) {
        if (!symbolCode) return '#9ca3af'; // gray
        const colorMap = {
            clearsky: '#fbbf24',      // yellow/amber for sun
            fair: '#fbbf24',          // yellow for fair weather
            partlycloudy: '#9ca3af',  // gray for clouds
            cloudy: '#9ca3af',        // gray
            rain: '#3b82f6',          // blue for rain
            lightrain: '#60a5fa',     // light blue
            heavyrain: '#1d4ed8',     // dark blue
            snow: '#93c5fd',          // light blue for snow
            lightsnow: '#bfdbfe',     // very light blue
            heavysnow: '#60a5fa',     // blue
            sleet: '#6b7280',         // gray
            fog: '#9ca3af',           // gray
            thunder: '#fbbf24',       // yellow for lightning
        };
        const baseSymbol = symbolCode.replace(/_day|_night/g, '');
        return colorMap[baseSymbol] || '#9ca3af';
    }

    formatTemp(temp) {
        if (temp == null) return '-';
        return `${Math.round(temp)}°`;
    }

    formatWind(wind) {
        if (wind == null) return '-';
        return `${Math.round(wind)} m/s`;
    }

    render() {
        const { weatherSummary, loading, error, containerWidth } = this.state;
        const { stores } = this.props;
        const { userStore } = stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        // Don't render if no weather data
        if (!loading && !weatherSummary) {
            return null;
        }

        if (loading) {
            return (
                <div class='w-100 d-flex flex-column mt-2 mb-1'>
                    <div class={`d-flex flex-row align-items-center justify-content-center mx-3 px-3 py-2 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <small class='text-muted'>
                            <i class='fa-solid fa-spinner fa-spin me-2' />
                            <Text id='race.loading-weather'>Loading weather...</Text>
                        </small>
                    </div>
                </div>
            );
        }

        if (error) {
            return null; // Silently fail
        }

        const { firstRecord, lastRecord, minTemp, maxTemp, minWind, maxWind, totalPrecipitation, mostCommonSymbol, recordCount, timeRange } = weatherSummary;
        const { tempData, windData, precipData, symbolMarkers } = this.prepareGraphData();
        const hasGraphData = tempData.length > 1;
        // Forecast records are built in-memory from yr.no and have no DB id,
        // so compare timeWindow (not id) to decide if start !== end.
        const showEndRecord = firstRecord && lastRecord
            && new Date(firstRecord.timeWindow).getTime() !== new Date(lastRecord.timeWindow).getTime();
        const hasForecast = timeRange?.end && new Date(timeRange.end).getTime() > Date.now();

        return (
            <div class='w-100 d-flex flex-column mt-2 mb-1'>
                <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    {/* Header row - stacks vertically on mobile */}
                    <div class='d-flex flex-column flex-md-row align-items-center justify-content-md-between px-3 py-2'>
                        {/* Weather icon and label */}
                        <div class='d-flex flex-row align-items-center mb-2 mb-md-0'>
                            <i class={`${this.getWeatherIcon(mostCommonSymbol)} text-primary me-2`} style='font-size: 1.5em;' />
                            <span class='text-muted font-weight-light'>
                                <Text id='race.weather-summary'>Weather</Text>
                            </span>
                            {hasForecast && (
                                <span class='badge bg-info ms-2'>
                                    <Text id='race.weather-forecast'>Forecast</Text>
                                </span>
                            )}
                        </div>

                        {/* From-To display - allow wrapping */}
                        <div class='d-flex flex-row flex-wrap align-items-center justify-content-center mb-2 mb-md-0'>
                            {/* Start weather */}
                            {firstRecord && (
                                <div class='d-flex flex-column align-items-center px-2'>
                                    <small class='text-muted' style='font-size: 0.7em;'>
                                        {util.formatDate(firstRecord.timeWindow, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                    </small>
                                    <div class='d-flex flex-row align-items-center'>
                                        <i class={`${this.getWeatherIcon(firstRecord.symbolCode)} me-1`} />
                                        <span class='font-weight-bold'>{this.formatTemp(firstRecord.temperature)}</span>
                                    </div>
                                    <small class='text-muted'>
                                        <i class='fa-solid fa-wind me-1' style='font-size: 0.8em;' />
                                        {this.formatWind(firstRecord.windSpeed)}
                                    </small>
                                </div>
                            )}

                            {/* Arrow */}
                            {showEndRecord && (
                                <div class='px-2'>
                                    <i class='fa-solid fa-arrow-right text-muted' />
                                </div>
                            )}

                            {/* End weather (if different from start) */}
                            {showEndRecord && (
                                <div class='d-flex flex-column align-items-center px-2'>
                                    <small class='text-muted' style='font-size: 0.7em;'>
                                        {util.formatDate(lastRecord.timeWindow, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                    </small>
                                    <div class='d-flex flex-row align-items-center'>
                                        <i class={`${this.getWeatherIcon(lastRecord.symbolCode)} me-1`} />
                                        <span class='font-weight-bold'>{this.formatTemp(lastRecord.temperature)}</span>
                                    </div>
                                    <small class='text-muted'>
                                        <i class='fa-solid fa-wind me-1' style='font-size: 0.8em;' />
                                        {this.formatWind(lastRecord.windSpeed)}
                                    </small>
                                </div>
                            )}
                        </div>

                        {/* Min/Max summary */}
                        <div class='d-flex flex-column align-items-center align-items-md-end'>
                            <div class='d-flex flex-row align-items-center'>
                                <small class='text-muted me-1'><i class='fa-solid fa-thermometer-half' /></small>
                                <small class='text-primary'>{this.formatTemp(minTemp)}</small>
                                <small class='text-muted mx-1'>-</small>
                                <small class='text-danger'>{this.formatTemp(maxTemp)}</small>
                            </div>
                            <div class='d-flex flex-row align-items-center'>
                                <small class='text-muted me-1'><i class='fa-solid fa-wind' /></small>
                                <small>{this.formatWind(minWind)}</small>
                                <small class='text-muted mx-1'>-</small>
                                <small>{this.formatWind(maxWind)}</small>
                            </div>
                            {totalPrecipitation > 0 && (
                                <div class='d-flex flex-row align-items-center'>
                                    <small class='text-muted me-1'><i class='fa-solid fa-cloud-rain' /></small>
                                    <small class='text-info'>{totalPrecipitation.toFixed(1)} mm</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Weather Graphs */}
                    {hasGraphData && (
                        <div class='d-flex flex-column px-2 pb-2' ref={el => { this.containerRef = el; }}>
                            {/* Combined Temperature & Wind Graph */}
                            <MiniGraph
                                stores={stores}
                                // Left axis: Temperature
                                dataLeft={[tempData]}
                                showYTicksLeft={1}
                                yTicksLeftVal={val => `${Math.round(val)}°`}
                                smoothLeft={[true]}
                                gradientFillLeft={['#3b82f6']}
                                inputStyleLeft={'stroke: #3b82f6; stroke-width: 3px;'}
                                // Right axis: Precipitation (bars) + Wind (line)
                                dataRight={[precipData, windData]}
                                displayTypeRight={['bar', 'line']}
                                showYTicksRight={1}
                                yTicksRightVal={val => `${Math.round(val)}`}
                                yTicksRightWidth='30px'
                                inputStyleRight={['fill: #60a5fa; opacity: 0.6;', 'stroke: #14b8a6; stroke-width: 2px;']}
                                barWidth={8}
                                barOpacity={0.6}
                                // Common config
                                width={containerWidth}
                                height={100}
                                paddingLeft={35}
                                paddingRight={30}
                                paddingTop={10}
                                paddingBottom={20}
                                showXTicks={1}
                                xTicksVal={val => util.formatDate(val, { hour: '2-digit', minute: '2-digit', hour12: false })}
                                xTicksCnt={4}
                                gradientOpacity={0.15}
                            />
                            {/* Legend for right axis */}
                            <div class='d-flex flex-row justify-content-end align-items-center px-2' style='margin-top: -4px;'>
                                <small class='me-3' style='font-size: 0.7em;'>
                                    <span style='display: inline-block; width: 12px; height: 8px; background: #60a5fa; opacity: 0.7; margin-right: 4px; vertical-align: middle;' />
                                    <span class='text-muted'>mm</span>
                                </small>
                                <small style='font-size: 0.7em;'>
                                    <span style='display: inline-block; width: 12px; height: 2px; background: #14b8a6; margin-right: 4px; vertical-align: middle;' />
                                    <span class='text-muted'>m/s</span>
                                </small>
                            </div>
                            {/* Weather icons below graph - positioned to match x-axis ticks */}
                            <div class='position-relative' style='height: 24px; margin-left: 35px; margin-right: 55px;'>
                                {symbolMarkers.map((marker, idx) => {
                                    // Simple percentage-based positioning within the margin-adjusted container
                                    const maxIndex = tempData.length - 1;
                                    const percentage = maxIndex > 0 ? (marker.index / maxIndex) * 100 : 0;
                                    return (
                                        <i
                                            key={idx}
                                            class={this.getWeatherIcon(marker.symbolCode)}
                                            style={`position: absolute; left: ${percentage}%; transform: translateX(-50%); font-size: 1em; color: ${this.getWeatherIconColor(marker.symbolCode)};`}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default RaceWeatherSummary;
